import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import {
  Box, Button, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, MenuItem, useTheme,
} from '@mui/material';

const CompanyFormDialog = ({ open, company, onClose, onSaved, isAdmin }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState({
    name: '', address: '', district: '', phone: '', contactPerson: '', email: '',
    status: 'новый', color: '#88c0d0',
    requisites: { inn: '', kpp: '', ogrn: '', bankName: '', bik: '', checkingAccount: '', correspondentAccount: '' },
    additionalInfo: '',
  });
  const [showNewStatusInput, setShowNewStatusInput] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  const presetColors = isDarkMode
    ? ['#88c0d0', '#a3be8c', '#ebcb8b', '#bf616a', '#b48ead', '#5e81ac']
    : ['#5e81ac', '#a3be8c', '#ebcb8b', '#bf616a', '#b48ead', '#88c0d0'];
  const defaultStatuses = ['новый', 'переговоры', 'звонок', 'заключение договора'];

  useEffect(() => {
    if (open) {
      if (company) {
        setFormData({
          name: company.name,
          address: company.address || '',
          district: company.district || '',
          phone: company.phone || '',
          contactPerson: company.contactPerson || '',
          email: company.email || '',
          status: company.status,
          color: company.color,
          requisites: company.requisites || { inn: '', kpp: '', ogrn: '', bankName: '', bik: '', checkingAccount: '', correspondentAccount: '' },
          additionalInfo: company.additionalInfo || '',
        });
      } else {
        setFormData({
          name: '', address: '', district: '', phone: '', contactPerson: '', email: '',
          status: 'новый', color: '#88c0d0',
          requisites: { inn: '', kpp: '', ogrn: '', bankName: '', bik: '', checkingAccount: '', correspondentAccount: '' },
          additionalInfo: '',
        });
      }
      setShowNewStatusInput(false);
      setNewStatus('');
    }
  }, [open, company]);

  const handleSubmit = async () => {
    try {
      if (company) {
        if (!isAdmin) {
          await axios.post('/api/change-requests', { companyId: company._id, changes: formData });
          alert('Заявка на изменение отправлена администратору на одобрение');
        } else {
          await axios.patch(`/api/companies/${company._id}`, formData);
        }
      } else {
        await axios.post('/api/companies', formData);
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error('Ошибка сохранения компании:', error);
      alert(error.response?.data?.message || 'Ошибка сохранения');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {company ? 'Редактировать компанию' : 'Новая компания'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Основная информация</Typography>

        <TextField fullWidth label="Название компании" value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} margin="normal" required />
        <TextField fullWidth label="Адрес" value={formData.address}
          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))} margin="normal" multiline rows={2} />
        <TextField fullWidth label="Район" value={formData.district}
          onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))} margin="normal" placeholder="Например: Центральный, Ленинский..." />
        <TextField fullWidth label="Номер телефона" value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} margin="normal" placeholder="+7 (___) ___-__-__" />
        <TextField fullWidth label="Контактное лицо (ФИО)" value={formData.contactPerson}
          onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))} margin="normal" />
        <TextField fullWidth label="Email" type="email" value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} margin="normal" />

        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Реквизиты предприятия</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth label="ИНН" value={formData.requisites.inn}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, inn: e.target.value } }))} margin="normal" />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="КПП" value={formData.requisites.kpp}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, kpp: e.target.value } }))} margin="normal" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="ОГРН" value={formData.requisites.ogrn}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, ogrn: e.target.value } }))} margin="normal" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Название банка" value={formData.requisites.bankName}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, bankName: e.target.value } }))} margin="normal" />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="БИК" value={formData.requisites.bik}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, bik: e.target.value } }))} margin="normal" />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Расчетный счет" value={formData.requisites.checkingAccount}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, checkingAccount: e.target.value } }))} margin="normal" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Корреспондентский счет" value={formData.requisites.correspondentAccount}
              onChange={(e) => setFormData(prev => ({ ...prev, requisites: { ...prev.requisites, correspondentAccount: e.target.value } }))} margin="normal" />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Дополнительная информация</Typography>
        <TextField fullWidth label="Заметки" value={formData.additionalInfo}
          onChange={(e) => setFormData(prev => ({ ...prev, additionalInfo: e.target.value }))} margin="normal" multiline rows={3}
          placeholder="Любая дополнительная информация о компании..." />

        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Статус компании</Typography>
        {showNewStatusInput ? (
          <Box>
            <TextField fullWidth label="Новый статус" value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)} margin="normal" placeholder="Введите название нового статуса" />
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="contained" size="small" onClick={() => {
                if (newStatus.trim()) { setFormData(prev => ({ ...prev, status: newStatus.trim() })); setNewStatus(''); setShowNewStatusInput(false); }
              }}>Добавить</Button>
              <Button variant="outlined" size="small" onClick={() => { setShowNewStatusInput(false); setNewStatus(''); }}>Отмена</Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <TextField fullWidth select label="Статус" value={formData.status}
              onChange={(e) => { if (e.target.value === 'CREATE_NEW') { setShowNewStatusInput(true); } else { setFormData(prev => ({ ...prev, status: e.target.value })); } }}
              margin="normal">
              {defaultStatuses.map((status) => (<MenuItem key={status} value={status}>{status}</MenuItem>))}
              <MenuItem value="CREATE_NEW" sx={{ color: 'primary.main', fontWeight: 600 }}>+ Создать новый статус</MenuItem>
            </TextField>
          </Box>
        )}

        <Box mt={2}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Цвет статуса</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            {presetColors.map((color) => (
              <Box key={color} sx={{
                width: 40, height: 40, backgroundColor: color, borderRadius: 2, cursor: 'pointer',
                border: formData.color === color ? '3px solid' : '2px solid',
                borderColor: formData.color === color ? (isDarkMode ? '#fff' : '#000') : 'rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                boxShadow: formData.color === color ? `0 0 20px ${color}60` : 'none',
                '&:hover': { transform: 'scale(1.1)' },
              }} onClick={() => setFormData(prev => ({ ...prev, color }))} />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">Отмена</Button>
        <Button onClick={handleSubmit} variant="contained">
          {company ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyFormDialog;
